import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import type {
  CatalogProduct,
  HomepageSingleCategoryProduct,
  SingleProduct,
  WishlistProduct,
} from "src/types/graphql.d";
import { SEGMENT_TOGGLE_WISHLIST_TYPE } from "src/segment";

type FavouritedProduct =
  | CatalogProduct
  | HomepageSingleCategoryProduct
  | SingleProduct
  | WishlistProduct;

type ProductFavouritedEventProps = {
  product: FavouritedProduct;
  currency: string;
  eventType: SEGMENT_TOGGLE_WISHLIST_TYPE;
  email?: string | null;
  resultId?: string;
};

type ProductFavouritedAnalyticsProps = {
  brand: string;
  brand_id: string;
  category: unknown;
  category_id: unknown;
  currency: string;
  image_url?: string | null;
  labels: string[];
  name: string;
  price: number;
  product_discount?: number | null;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  regular_price: number;
  sizes_available?: unknown;
  slug: string;
  url: string;
  gender?: string | null;
  email?: string | null;
  result_id?: string;
};

export type ProductFavouritedEvent = TrackingEvent<
  | "Product Favourited"
  | "Product Unfavourited"
  | "Product Favourited When Logged-Out",
  ProductFavouritedAnalyticsProps
>;

function getEventName(
  eventType: SEGMENT_TOGGLE_WISHLIST_TYPE
):
  | "Product Favourited"
  | "Product Unfavourited"
  | "Product Favourited When Logged-Out" {
  if (eventType === SEGMENT_TOGGLE_WISHLIST_TYPE.favouritedWhenLoggedOut) {
    return "Product Favourited When Logged-Out";
  }
  if (eventType === SEGMENT_TOGGLE_WISHLIST_TYPE.favourited) {
    return "Product Favourited";
  }
  return "Product Unfavourited";
}

export async function trackProductFavouritedEvent({
  product,
  currency,
  eventType,
  email,
  resultId,
}: ProductFavouritedEventProps): TrackingEventResponse {
  const { images, variations } = product as SingleProduct;

  const { thumbnail } = product as
    | CatalogProduct
    | HomepageSingleCategoryProduct
    | WishlistProduct;

  const { available_sizes } = product as CatalogProduct;

  const { sizes_available } = product as
    | HomepageSingleCategoryProduct
    | WishlistProduct;

  const sizesAvailable =
    available_sizes ||
    sizes_available ||
    (variations || []).map((variation) => variation.size);

  const imageUrl = images ? images[0] : thumbnail;

  const { price: catalogProductPrice, regular_price } = product as
    | CatalogProduct
    | SingleProduct
    | WishlistProduct;

  const { gender } = product as SingleProduct;

  const {
    price_float: homepageSingleCategoryProductPrice,
    regular_price_float: homepageSingleCategoryProductRegularPrice,
  } = product as HomepageSingleCategoryProduct;

  const price = catalogProductPrice || homepageSingleCategoryProductPrice;
  const regularPrice =
    regular_price || homepageSingleCategoryProductRegularPrice;

  return await trackEvent(getEventName(eventType), {
    props: {
      brand: product.brand.name,
      brand_id: product.brand.id,
      category: product.translated_category_names,
      category_id: product.translated_category_ids,
      currency,
      image_url: imageUrl,
      labels: (product.labels || []).map(({ value }) => value),
      name: product.name,
      price,
      product_discount: product?.product_discount,
      product_id: product.id,
      product_kids_gender: product.product_shop_type?.includes("kids")
        ? product.product_kids_gender
        : undefined,
      product_shop_type: product.product_shop_type,
      regular_price: regularPrice,
      sizes_available: sizesAvailable,
      slug: product.slug,
      url: `/product/${product.slug}`,
      gender,
      email,
      result_id: resultId,
    },
    withRequestSource: true,
  });
}
